import React from "react";
import "./ProductsDrawer.less";
import { DrawerProps } from "antd/lib/drawer";
import { FormComponentProps } from "@ant-design/compatible/lib/form";
import { Form } from "@ant-design/compatible";
import ZButton from "@atoms/ZButton";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import Input from "antd/lib/input";
import TextArea from "antd/lib/input/TextArea";
import { ZSelect } from "@atoms";
import {
	ProductPricingPlan,
	ProductBillingType,
	productBillingTypeOptions
} from "@/Contracts/Products";
import { createGuid } from "@/Contracts/Utils";
import {
	handleMarkupMargin,
	handleMarkupChange,
	handleMarginChange
} from "../../Utils/PricingCalculator";
import { useSelector } from "react-redux";
import ZTypography from "@atoms/ZTypography";
import InputNumber from "antd/lib/input-number";
import { GlobalState } from "@/Reducers/interface";
import ZDrawer from "@molecules/ZDrawer";
import { useCatalogPriceConversion } from "@/Hooks/useCatalogPriceConversion";

const { ZText } = ZTypography;

export interface Props
	extends DrawerProps,
		FormComponentProps<ProductPricingPlan> {
	readonly isLoading: boolean;
	readonly onSubmit: (addProductPricingPlan: ProductPricingPlan) => void;
}

export const AddProductPricingPlanDrawerForm: React.FC<Props> = props => {
	const priceConversion = useCatalogPriceConversion();
	const { defaultPreferences } = useSelector((state: GlobalState) => {
		return {
			defaultPreferences:
				state.user.userData?.user_settings?.preference_settings
					?.product_preference_settings
		};
	});

	const onClose = (
		$event: React.MouseEvent<any> | React.KeyboardEvent<HTMLDivElement>
	) => {
		props.onClose?.($event as any);
	};

	const onSubmit = ($event: React.FormEvent<HTMLFormElement>) => {
		$event.preventDefault();
		props.form.validateFields(
			(errors: unknown, addProductPricingPlan: ProductPricingPlan) => {
				if (!errors) {
					addProductPricingPlan.session_id = createGuid();
					if (priceConversion.active) {
						addProductPricingPlan.cost_price =
							priceConversion.fromDisplay(
								addProductPricingPlan.cost_price
							);
						addProductPricingPlan.sell_price =
							priceConversion.fromDisplay(
								addProductPricingPlan.sell_price
							);
						addProductPricingPlan.setup_price =
							priceConversion.fromDisplay(
								addProductPricingPlan.setup_price
							);
					}
					props.onSubmit(addProductPricingPlan);
				}
			}
		);
	};

	const getFieldValues = () => {
		return props.form.getFieldsValue([
			"cost_price",
			"sell_price",
			"should_add_setup_price_to_margin",
			"setup_price",
			"margin",
			"markup",
			"vendor_name",
			"vendor_sku_no"
		]);
	};

	const onCostPriceChange = (newCostPrice?: number | string | null): void => {
		if (newCostPrice === undefined || newCostPrice === null) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupMargin(
				Number(newCostPrice),
				values.sell_price,
				values.quantity,
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
	};

	const onMarkupChange = (newMarkup?: number | string | null): void => {
		if (
			newMarkup === undefined ||
			props.form.getFieldValue("cost_price") === 0
		) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupChange(values, Number(newMarkup))
		);
	};

	const onMarginChange = (newMargin?: number | string | null): void => {
		if (
			newMargin === undefined ||
			newMargin === null ||
			props.form.getFieldValue("cost_price") === 0
		) {
			return;
		}
		const values = getFieldValues();

		if (
			Number(newMargin) >= 100 &&
			Number(getFieldValues().cost_price) !== 0
		) {
			return;
		}
		props.form.setFieldsValue(
			handleMarginChange(values, Number(newMargin))
		);
	};

	const onSellPriceChange = (newSellPrice?: number | string | null): void => {
		if (newSellPrice === undefined || newSellPrice === null) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupMargin(
				values.cost_price,
				Number(newSellPrice),
				values.quantity,
				values.should_add_setup_price_to_margin,
				values.setup_price
			)
		);
	};

	const onSetupPriceChange = (
		newSetupPrice?: number | string | null
	): void => {
		if (newSetupPrice === undefined || newSetupPrice === null) {
			return;
		}
		const values = getFieldValues();
		if (!values.should_add_setup_price_to_margin) {
			return;
		}
		props.form.setFieldsValue(
			handleMarkupMargin(
				values.cost_price,
				values.sell_price,
				values.quantity,
				values.should_add_setup_price_to_margin,
				Number(newSetupPrice)
			)
		);
	};

	return (
		<ZDrawer
			visible={props.visible}
			closable={true}
			destroyOnClose={true}
			onClose={onClose}
			className="add-pricing-plan-drawer zomentum-drawer"
			title={
				<Row>
					<Col span={24}>
						<div className="drawer-title-container">
							<ZText className="drawer-title">
								Add Pricing Plan
							</ZText>
						</div>
					</Col>
				</Row>
			}
		>
			<Form
				layout="horizontal"
				labelCol={{ span: 5 }}
				wrapperCol={{ span: 19 }}
				onSubmit={onSubmit}
				className="add-pricing-plan-drawer-form"
			>
				<Row className="drawer-form-container" gutter={[10, 20]}>
					<Col span={24}>
						<Form.Item
							label="Name"
							labelAlign="left"
							hasFeedback={true}
						>
							<Input.Group>
								{props.form.getFieldDecorator("name", {
									rules: [
										{
											required: true,
											message:
												"Please enter pricing plan name",
											whitespace: true
										}
									],
									initialValue: "Default Pricing"
								})(
									<Input placeholder="Please enter pricing plan name" />
								)}
							</Input.Group>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							label="Description"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("description", {
								rules: [
									{
										required: false,
										message:
											"Please enter pricing plan description",
										whitespace: true
									}
								],
								initialValue: ""
							})(
								<TextArea
									placeholder="Please enter pricing plan description"
									autoSize={{ minRows: 3 }}
									allowClear={true}
									className="zomentum-sm-textarea"
								/>
							)}
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							label="Billing Period"
							labelAlign="left"
							hasFeedback={true}
							colon={false}
						>
							{props.form.getFieldDecorator("billing_period", {
								rules: [
									{
										required: true,
										message: "Please select billing period"
									}
								],
								initialValue: ProductBillingType.OneTime
							})(
								<ZSelect<ProductBillingType>
									allowClear={true}
									className="w-5/6"
									getPopupContainer={trigger =>
										trigger.parentNode
									}
									placeholder="Please select billing period"
									selectOptions={(
										Object.keys(
											productBillingTypeOptions
										) as ProductBillingType[]
									).map((value, index) => ({
										name: productBillingTypeOptions[value],
										value: value,
										key: `billiing-period-${index}`
									}))}
								/>
							)}
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							label="Cost Price"
							labelAlign="left"
							hasFeedback={true}
							colon={false}
						>
							<Input.Group>
								{props.form.getFieldDecorator("cost_price", {
									rules: [
										{
											required: true,
											validator: (_, value) => {
												if (Number(value) < 0) {
													return Promise.reject(
														new Error(
															"Invalid Entry. Cost price cannot be less than 0"
														)
													);
												}
												if (!value && value !== 0) {
													return Promise.reject(
														new Error(
															"Cost price cannot be empty"
														)
													);
												}
												return Promise.resolve();
											}
										}
									],
									initialValue: 0
								})(
									<InputNumber
										precision={2}
										step={0.01}
										type="number"
										placeholder="Please enter cost price"
										onChange={onCostPriceChange}
									/>
								)}
								<Input
									placeholder={priceConversion.addonCurrency}
									disabled
									className="currency-addon"
								/>
							</Input.Group>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							label="Markup"
							labelAlign="left"
							hasFeedback={true}
							colon={false}
						>
							<Input.Group>
								{props.form.getFieldDecorator("markup", {
									rules: [
										{
											required: false,
											type: "number"
										}
									],
									initialValue:
										defaultPreferences?.markup ?? 0
								})(
									<InputNumber
										placeholder="Please enter product Markup"
										precision={2}
										onChange={onMarkupChange}
									/>
								)}
								<Input
									placeholder={"%"}
									disabled
									className="currency-addon"
								/>
							</Input.Group>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							label="Margin"
							labelAlign="left"
							hasFeedback={true}
							colon={false}
						>
							<Input.Group>
								{props.form.getFieldDecorator("margin", {
									rules: [
										{
											required: false,
											validator: (_, value) => {
												if (
													Number(value) >= 100 &&
													props.form.getFieldValue(
														"cost_price"
													) > 0
												) {
													return Promise.reject(
														new Error(
															"Invalid Entry. Margin cannot be 100% unless cost price is 0"
														)
													);
												}
												return Promise.resolve();
											}
										}
									],
									initialValue:
										defaultPreferences?.margin ?? 0
								})(
									<InputNumber
										placeholder="Please enter product Margin"
										max={100}
										precision={2}
										onChange={onMarginChange}
									/>
								)}
								<Input
									placeholder={"%"}
									disabled
									className="currency-addon"
								/>
							</Input.Group>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							label="Selling Price"
							labelAlign="left"
							hasFeedback={true}
							colon={false}
						>
							<Input.Group>
								{props.form.getFieldDecorator("sell_price", {
									rules: [
										{
											required: true,
											message:
												"Please enter selling price"
										}
									],
									initialValue: 0
								})(
									<InputNumber
										precision={2}
										step={0.01}
										type="number"
										placeholder="Please enter selling price"
										min={0}
										onChange={onSellPriceChange}
									></InputNumber>
								)}
								<Input
									placeholder={priceConversion.addonCurrency}
									disabled
									className="currency-addon"
								/>
							</Input.Group>
						</Form.Item>
					</Col>
					<Col span={24} className="mb-3">
						<Form.Item
							label="Setup Fee"
							labelAlign="left"
							hasFeedback={true}
							colon={false}
						>
							<Input.Group>
								{props.form.getFieldDecorator("setup_price", {
									rules: [
										{
											required: true,
											message: "Please enter setup fee"
										}
									],
									initialValue: 0
								})(
									<InputNumber
										precision={2}
										step={0.01}
										type="number"
										placeholder="Please enter setup fee"
										min={0}
										onChange={onSetupPriceChange}
									/>
								)}
								<Input
									placeholder={priceConversion.addonCurrency}
									disabled
									className="currency-addon"
								/>
							</Input.Group>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							label="Vendor Name"
							labelAlign="left"
							hasFeedback={true}
							colon={false}
						>
							<Input.Group>
								{props.form.getFieldDecorator("vendor_name", {
									rules: [],
									initialValue: ""
								})(
									<Input placeholder="Please enter Vendor Name" />
								)}
							</Input.Group>
						</Form.Item>
					</Col>
					<Col span={24} className="mb-4">
						<Form.Item
							label="Vendor SKU NO."
							labelAlign="left"
							hasFeedback={true}
						>
							<Input.Group>
								{props.form.getFieldDecorator("vendor_sku_no", {
									rules: [
										{
											required: false,
											message:
												"Please enter Vendor SKU No",
											whitespace: true
										}
									],
									initialValue: ""
								})(
									<Input placeholder="Please enter Vendor SKU No" />
								)}
							</Input.Group>
						</Form.Item>
					</Col>
				</Row>
				<div className="ant-drawer-footer">
					<ZButton
						type="default"
						htmlType="button"
						onClick={onClose}
						disabled={props.isLoading}
						className="mr-4"
					>
						Cancel
					</ZButton>
					<ZButton
						type="primary"
						htmlType="submit"
						disabled={props.isLoading}
						loading={props.isLoading}
						data-testid="pricing-submit-button"
					>
						Save
					</ZButton>
				</div>
			</Form>
		</ZDrawer>
	);
};

export default Form.create<Props>({ name: "add_product_pricing_plan_form" })(
	AddProductPricingPlanDrawerForm
);
